import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@dia/ui';
import { useEffect, type ReactNode } from 'react';
import { useLang } from '@dia/i18n';
import { useAuth } from './auth';
import { useIdentity } from './screens/lib/identity';
import { defaultLangFor, rememberDefaultLang } from './screens/lib/roleLang';
import { SignIn } from './screens/SignIn';
import { Pair } from './screens/Pair';
import { Hoy } from './screens/Hoy';
import { OwnerSignIn } from './screens/OwnerSignIn';
import { OwnerResetPassword } from './screens/OwnerResetPassword';
import { Limpieza } from './screens/Limpieza';
import { LimpiezaRun } from './screens/LimpiezaRun';
import { Mantenimiento } from './screens/Mantenimiento';
import { Habitaciones } from './screens/Habitaciones';
import { Sistemas } from './screens/Sistemas';
import { SistemasAsset } from './screens/SistemasAsset';
import { Mensajes } from './screens/Mensajes';
import { MensajesHilo } from './screens/MensajesHilo';
import { Traductor } from './screens/Traductor';
import { Mas } from './screens/Mas';
import { Energia } from './screens/Energia';
import { Jefe } from './screens/Jefe';
import { JefeEstancias } from './screens/JefeEstancias';
import { JefeTelefonos } from './screens/JefeTelefonos';
import { JefeRevisar } from './screens/JefeRevisar';
import { SinEnviar } from './screens/SinEnviar';
import { NotFound } from './screens/NotFound';

/**
 * The routing skeleton. Section 7 Phase 1: Hoy is the home screen at `/`, and
 * Limpieza, Mantenimiento, Sistemas, Mensajes and Traductor sit beside it,
 * reached from the tab bar and the Mas list. Which of them a person sees
 * is decided by their role (Section 4), and the database enforces the same
 * rule (migration 023).
 *
 * Everything except pairing needs a session. There is no public ops screen.
 */
export function App() {
  const { session } = useAuth();

  return (
    <BrowserRouter>
      <RoleLanguage />
      <ScrollToTop />
      <RouteBoundary>
        <Routes>
          <Route path="/registrar" element={<Pair />} />
          <Route path="/entrar" element={session ? <Navigate to="/" replace /> : <SignIn />} />
          <Route path="/dueno" element={<OwnerSignIn />} />
          <Route path="/dueno/restablecer" element={<OwnerResetPassword />} />
          {/* Each screen below guards itself: a staff session or an owner session gets in. */}
          <Route path="/" element={<Hoy />} />
          {/* The old address of Hoy, kept so a saved link still lands on it. */}
          <Route path="/hoy" element={<Navigate to="/" replace />} />
          <Route path="/limpieza" element={<Limpieza />} />
          <Route path="/limpieza/:runId" element={<LimpiezaRun />} />
          <Route path="/mantenimiento" element={<Mantenimiento />} />
          <Route path="/habitaciones" element={<Habitaciones />} />
          <Route path="/sistemas" element={<Sistemas />} />
          <Route path="/sistemas/:slug" element={<SistemasAsset />} />
          <Route path="/mensajes" element={<Mensajes />} />
          <Route path="/mensajes/huesped/:stayId" element={<MensajesHilo />} />
          <Route path="/mensajes/:requestId" element={<MensajesHilo />} />
          <Route path="/traductor" element={<Traductor />} />
          <Route path="/energia" element={<Energia />} />
          <Route path="/mas" element={<Mas />} />
          {/* The parking bay for writes the island refused (defect D5). */}
          <Route path="/sin-enviar" element={<SinEnviar />} />
          <Route path="/jefe" element={<Jefe />} />
          <Route path="/jefe/estadias" element={<JefeEstancias />} />
          <Route path="/jefe/telefonos" element={<JefeTelefonos />} />
          <Route path="/jefe/revisar" element={<JefeRevisar />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </RouteBoundary>
    </BrowserRouter>
  );
}

/**
 * English for owners and managers, Spanish for the crew, until a person chooses
 * for themselves with the toggle. Renders nothing. Signed out, it leaves the
 * last default alone, so a manager's sign-in screen does not flip languages.
 */
function RoleLanguage() {
  const { identity } = useIdentity();
  const { setDefaultLang } = useLang();
  const role = identity?.role;

  useEffect(() => {
    if (!role) return;
    const lang = defaultLangFor(role);
    setDefaultLang(lang);
    rememberDefaultLang(lang);
  }, [role, setDefaultLang]);

  return null;
}

/**
 * A screen that throws while drawing shows a message and a retry button rather
 * than a blank page, and moving to another route clears it (resetKey), so one
 * bad screen never traps the person on it.
 */
function RouteBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.key}>{children}</ErrorBoundary>;
}

/**
 * A new page opens at its own top, not wherever the last page's scroll
 * happened to be. React Router does not do this on its own, so a heading
 * tapped from partway down the Mas list opened the target screen already
 * scrolled to its middle or bottom (reported 2026-09-26, owner console).
 * Same fix as the guest app's App.tsx.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}
